/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-syntax */
import { useMemo, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import { fetchPresubscribedBlocks } from '@actions/goal';
import CarouselFeedBlock from '@cont/FeedTypeBlock/CarouselFeedBlock';
import { toggleEnrolledCourse } from '@actions/courses';
import Dynamic from '@base/Dynamic/Dynamic';
import Banner from '@cont/TalkToUnacademy/Banner';
import useWindowSize from '@hooks/useWindowSize';
import IconicSection from './Iconic/IconicSection';
import SecondSection from './SecondSection';
import ThirdSection from './ThirdSection';
import PageHead from './PageHead';
import FourthSection from './FourthSection';
import GenericBanner from './GenericBanner';
import SkeletonLoading from './Loading';

const FAQSection = Dynamic(() => import('./FAQSection'));
const PresubscribedLegendsSection = Dynamic(() =>
  import('./PresubscribedLegendsSection')
);
const UpcomingSection = Dynamic(() => import('./UpcomingSection'));
const TopicsSection = Dynamic(() => import('./TopicsSection'));
const TestSeriesSection = Dynamic(() => import('./TestSeriesSection'));
const SubscriptionsSection = Dynamic(() => import('./SubscriptionsSection'));
const SubscriptionBenefitsSection = Dynamic(() =>
  import('./SubscriptionBenefitsSection')
);
const ReviewsSection = Dynamic(() => import('./ReviewsSection'));
const LearnOnTheGo = Dynamic(() => import('@comp/LearnOnTheGo/LearnOnTheGo'));

const Container = styled.div`
  margin-bottom: 160px;
  @media (max-width: 70.95em) {
    margin-bottom: 120px;
  }
  @media (max-width: 47.95em) {
    padding: 48px 0 0;
    margin-bottom: 80px;
    overflow: hidden;
    grid-column: span 11;
    &.p0 {
      padding: 0;
    }
  }
`;

const styledLearn = styled(LearnOnTheGo)`
  margin-top: 0;
`;

const BannerWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-bottom: 124px;
  @media (max-width: 70.95em) {
    width: 728px;
    margin-bottom: 80px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    display: none;
  }
`;

const GenericBannerWrapper = (bannerData) => (
  <BannerWrapper>
    <GenericBanner block={bannerData.block} />
  </BannerWrapper>
);

const OneForAllCarousel = ({ block }) => {
  const dispatch = useDispatchToStore();
  const enroll = (course) => () =>
    dispatch(toggleEnrolledCourse(course.uid, !course.isEnrolled));
  return (
    <BannerWrapper>
      <CarouselFeedBlock
        block={block}
        preSubscribed
        gridColumn="span 24"
        itemWidth="1136px"
        imgWidth="52%"
        leftOverlay="200px"
        enroll={enroll}
      />
    </BannerWrapper>
  );
};

const PlusHome = ({ preSubscribedBlocks, isSubscribed }) => {
  const mounted = typeof window !== 'undefined';
  const scrolled = useRef(false);
  const isGamma = process.env.ENVIRONMENT === 'gamma';
  const dispatch = useDispatch();
  const router = useRouter();
  const topologiesData = useSelectorToStore(
    ({ topologies }) => topologies.data ?? {}
  );
  const isGoalEnabled = useSelectorToStore(
    ({ ttu }) => ttu.showData?.is_goal_enabled || false
  );
  const me = useSelectorToStore(({ auth }) => auth.me);
  const { isMobile } = useWindowSize();
  let { goalUID, goalSlug } = router.query;
  goalUID = goalUID.toUpperCase();
  const goalDetails = goalUID ? topologiesData[goalUID] : null;
  const countryCode = goalUID === 'QZIXN' ? 'BRL' : 'INR';
  const countryName = countryCode === 'INR' ? 'India' : 'Brazil';

  const hasIconicSection = !!preSubscribedBlocks.find(
    (obj) => obj.type === 'iconic_benefits'
  );

  const handleScroll = () => {
    if (!scrolled.current) {
      dispatch(fetchPresubscribedBlocks(goalUID, 20, isGamma ? 3 : 5));
      scrolled.current = true;
    }
  };

  useEffect(() => {
    if (preSubscribedBlocks.length > 7) {
      scrolled.current = true;
    }
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      scrolled.current = false;
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const partPaymentStatus = useSelectorToStore(
    ({ checkout }) => checkout.partPaymentStatus?.[goalUID]
  );

  const renderBlocks = useMemo(() => {
    const sections = {
      subscription_top: PageHead,
      subscription_benefits: SecondSection,
      iconic_benefits: IconicSection,
      legends_on_unacademy: PresubscribedLegendsSection,
      special_classes: ThirdSection,
      educators: FourthSection,
      top_educators: FourthSection,
      test_series: TestSeriesSection,
      testseries: TestSeriesSection,
      extra_subscription_benefits: SubscriptionBenefitsSection,
      subscriptions: partPaymentStatus?.is_active_part_set
        ? null
        : SubscriptionsSection,
      faq: partPaymentStatus?.is_active_part_set ? null : FAQSection,
      upcoming: UpcomingSection,
      courses: UpcomingSection,
      app_download: styledLearn,
      topics: TopicsSection,
      reviews: ReviewsSection,
      batches: UpcomingSection,
      generic_banner: GenericBannerWrapper,
      carousel_feed: OneForAllCarousel
    };
    if (goalUID === 'LEARNCP') {
      sections.subscription_benefits = IconicSection;
    }
    const getSections = (type, id) => {
      for (const key in sections) {
        if (
          key.includes(type.toLowerCase()) ||
          (id && key.includes(id.toLowerCase()))
        ) {
          return sections[key];
        }
      }
    };
    return preSubscribedBlocks?.map((block) => {
      const { type, extraBlockinfo, data } = block;
      const { id } = extraBlockinfo;

      const Component = getSections(type, id);
      const tab = block.extraBlockinfo?.tabs?.[0];
      const url = tab?.url;
      let hrefURL = null;
      let asURL = null;
      if (url && data?.length > 3) {
        if (type.includes('topic') || id.includes('topic')) {
          hrefURL = '/goal/[goalSlug]/[goalUID]/topics';
          asURL = `/goal/${goalSlug}/${goalUID}/topics`;
        } else if (type.includes('test_series') || id.includes('test_series')) {
          hrefURL = '/goal/[goalSlug]/[goalUID]/test-series';
          asURL = `/goal/${goalSlug}/${goalUID}/test-series`;
        } else if (type === 'batches') {
          hrefURL = '/goal/[goalSlug]/[goalUID]/batches';
          asURL = `/goal/${goalSlug}/${goalUID}/batches`;
        } else {
          hrefURL = '/goal/[goalSlug]/[goalUID]/[type]';
          asURL = `/goal/${goalSlug}/${goalUID}/${type}`;
        }
      }

      if (Component)
        return (
          <Component
            key={block.type}
            type={block.type}
            block={block}
            goalUID={goalUID}
            goalSlug={goalSlug}
            goalDetails={goalDetails}
            countryCode={countryCode}
            countryName={countryName}
            href={hrefURL}
            as={asURL}
            dataAnalytics="goal"
            hasIconicSection={hasIconicSection}
            isSubscribed={isSubscribed}
          />
        );
      return null;
    });
  }, [preSubscribedBlocks.length]);
  return (
    <Container
      id="preSubscribedBlocks"
      className={me.uid && isSubscribed ? 'p0' : ''}
    >
      {renderBlocks}
      {isGoalEnabled && !me?.plusSubscriptions?.length && !isMobile && (
        <Banner goalDetails={goalDetails} />
      )}
      {!scrolled.current &&
        mounted &&
        preSubscribedBlocks.length < 7 &&
        [1, 2, 3].map((item) => <SkeletonLoading key={item} />)}
    </Container>
  );
};

export default PlusHome;

PlusHome.propTypes = {
  preSubscribedBlocks: PropTypes.arrayOf(PropTypes.any).isRequired,
  isSubscribed: PropTypes.bool.isRequired
};

OneForAllCarousel.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired
};
